// Print Report Service for Professional Receipts and Reports
// Includes company logo, branch information, and audit-friendly formatting

export interface CompanyInfo {
  name: string;
  address: string;
  city: string;
  postalCode: string;
  country: string;
  phone: string;
  email: string;
  website: string;
  taxId: string;
  vatNumber: string;
  branchName: string;
  branchAddress: string;
  branchPhone: string;
  logoUrl: string;
}

export interface PaymentReportData {
  paymentId: string;
  orderId: string;
  customerName: string;
  amount: number;
  paymentMethod: string;
  status: string;
  transactionId: string;
  date: string;
  time: string;
  items?: Array<{
    name: string;
    quantity: number;
    price: number;
    total: number;
  }>;
  subtotal?: number;
  tax?: number;
  total: number;
  cashier?: string;
  branchInfo?: string;
}

export interface OrderReportData {
  orderId: string;
  orderNumber: string;
  customerName: string;
  status: string;
  date: string;
  time: string;
  items: Array<{
    name: string;
    quantity: number;
    price: number;
    total: number;
  }>;
  subtotal: number;
  tax: number;
  total: number;
  cashier?: string;
  branchInfo?: string;
  order_type?: 'dine_in' | 'takeaway' | 'delivery';
  customerAddress?: string;
  tableNumber?: string | number;
}

// Company Information - Update this with your client's details
export const COMPANY_INFO: CompanyInfo = {
  name: "SAHEEH UG",
  address: "Sudetenstraße 60",
  city: "Hürth",
  postalCode: "50354",
  country: "Germany",
  phone: "022337063324",
  email: "info@saheehpos.com",
  website: "www.saheehpos.com",
  taxId: "DE123456789",
  vatNumber: "DE123456789",
  branchName: "Saheeh Hürth",
  branchAddress: "Sudetenstraße 60, 50354 Hürth",
  branchPhone: "022337063324",
  logoUrl: "/saheeh_logo_w.svg"
};

export class PrintReportService {
  private static instance: PrintReportService;
  
  public static getInstance(): PrintReportService {
    if (!PrintReportService.instance) {
      PrintReportService.instance = new PrintReportService();
    }
    return PrintReportService.instance;
  }

  // Generate Payment Receipt HTML
  public generatePaymentReceipt(data: PaymentReportData): string {
    const currentDate = new Date();
    const formattedDate = currentDate.toLocaleDateString('en-US');
    const formattedTime = currentDate.toLocaleTimeString('en-US');
    
    return `
<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Zahlungsbeleg - ${data.orderId}</title>
    <style>
        @page {
            size: A4;
            margin: 0.5in;
        }
        
        * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
        }
        
        body {
            font-family: 'Arial', sans-serif;
            font-size: 12px;
            line-height: 1.4;
            color: #000;
            background: #fff;
        }
        
        .receipt {
            max-width: 100%;
            margin: 0 auto;
            padding: 20px;
            border: 2px solid #000;
        }
        
        .header {
            text-align: center;
            margin-bottom: 20px;
            border-bottom: 2px solid #000;
            padding-bottom: 15px;
        }
        
        .logo {
            max-width: 150px;
            height: auto;
            margin-bottom: 10px;
        }
        
        .company-name {
            font-size: 24px;
            font-weight: bold;
            margin-bottom: 5px;
            text-transform: uppercase;
        }
        
        .company-address {
            font-size: 11px;
            margin-bottom: 5px;
        }
        
        .branch-info {
            font-size: 12px;
            font-weight: bold;
            margin-top: 10px;
            padding: 8px;
            background-color: #f0f0f0;
            border: 1px solid #000;
        }
        
        .receipt-title {
            font-size: 18px;
            font-weight: bold;
            text-align: center;
            margin: 20px 0;
            text-transform: uppercase;
            border: 2px solid #000;
            padding: 10px;
            background-color: #e0e0e0;
        }
        
        .receipt-info {
            margin-bottom: 20px;
        }
        
        .info-row {
            display: flex;
            justify-content: space-between;
            margin-bottom: 5px;
            padding: 3px 0;
        }
        
        .info-label {
            font-weight: bold;
            min-width: 120px;
        }
        
        .info-value {
            text-align: right;
        }
        
        .amount-section {
            border: 2px solid #000;
            padding: 15px;
            margin: 20px 0;
            background-color: #f9f9f9;
        }
        
        .amount-row {
            display: flex;
            justify-content: space-between;
            margin-bottom: 8px;
            font-size: 14px;
        }
        
        .total-row {
            font-size: 18px;
            font-weight: bold;
            border-top: 2px solid #000;
            padding-top: 10px;
            margin-top: 10px;
        }
        
        .status-section {
            text-align: center;
            margin: 20px 0;
            padding: 15px;
            border: 2px solid #000;
            background-color: ${data.status === 'completed' ? '#d4edda' : '#f8d7da'};
        }
        
        .status-text {
            font-size: 16px;
            font-weight: bold;
            color: ${data.status === 'completed' ? '#155724' : '#721c24'};
        }
        
        .footer {
            margin-top: 30px;
            text-align: center;
            font-size: 10px;
            border-top: 1px solid #000;
            padding-top: 15px;
        }
        
        .footer-info {
            margin-bottom: 5px;
        }
        
        .audit-info {
            margin-top: 20px;
            padding: 10px;
            background-color: #f0f0f0;
            border: 1px solid #000;
            font-size: 10px;
        }
        
        .audit-title {
            font-weight: bold;
            margin-bottom: 5px;
            text-decoration: underline;
        }
        
        .print-date {
            text-align: right;
            font-size: 10px;
            margin-top: 10px;
            color: #666;
        }
        
        @media print {
            body { -webkit-print-color-adjust: exact; }
            .receipt { border: none; }
        }
    </style>
</head>
<body>
    <div class="receipt">
        <!-- Header with Logo and Company Info -->
        <div class="header">
            <img src="${COMPANY_INFO.logoUrl}" alt="SAHEEH Logo" class="logo" onerror="this.style.display='none'">
            <div class="company-name">${COMPANY_INFO.name}</div>
            <div class="company-address">
                ${COMPANY_INFO.address}<br>
                ${COMPANY_INFO.postalCode} ${COMPANY_INFO.city}<br>
                ${COMPANY_INFO.country}
            </div>
            <div class="company-address">
                Tel: ${COMPANY_INFO.phone} | Email: ${COMPANY_INFO.email}<br>
                Web: ${COMPANY_INFO.website}
            </div>
            <div class="branch-info">
                <strong>Filiale:</strong> ${COMPANY_INFO.branchName}<br>
                <strong>Adresse:</strong> ${COMPANY_INFO.branchAddress}<br>
                <strong>Tel:</strong> ${COMPANY_INFO.branchPhone}
            </div>
        </div>
        
        <!-- Receipt Title -->
        <div class="receipt-title">Zahlungsbeleg</div>
        
        <!-- Receipt Information -->
        <div class="receipt-info">
            <div class="info-row">
                <span class="info-label">Beleg-Nr:</span>
                <span class="info-value">${data.paymentId}</span>
            </div>
            <div class="info-row">
                <span class="info-label">Bestell-Nr:</span>
                <span class="info-value">${data.orderId}</span>
            </div>
            <div class="info-row">
                <span class="info-label">Kunde:</span>
                <span class="info-value">${data.customerName || 'Kein Name eingegeben'}</span>
            </div>
            <div class="info-row">
                <span class="info-label">Datum:</span>
                <span class="info-value">${data.date}</span>
            </div>
            <div class="info-row">
                <span class="info-label">Uhrzeit:</span>
                <span class="info-value">${data.time}</span>
            </div>
            <div class="info-row">
                <span class="info-label">Kassierer:</span>
                <span class="info-value">${data.cashier || 'System'}</span>
            </div>
        </div>
        
        <!-- Amount Section -->
        <div class="amount-section">
            <div class="amount-row">
                <span>Zahlungsmethode:</span>
                <span>${(() => {
                  // CRITICAL: Show correct payment method on payment report
                  // cash → "CASH", card → "KARTENZAHLUNG", paypal → "PAYPAL" or "ONLINE"
                  if (data.paymentMethod === 'cash') {
                    return 'CASH';
                  } else if (data.paymentMethod === 'card') {
                    return 'KARTENZAHLUNG';
                  } else if (data.paymentMethod === 'paypal') {
                    return 'PAYPAL';
                  } else {
                    return data.paymentMethod.toUpperCase();
                  }
                })()}</span>
            </div>
            <div class="amount-row">
                <span>Transaktions-ID:</span>
                <span>${data.transactionId}</span>
            </div>
            <div class="amount-row">
                <span>Betrag (netto):</span>
                <span>€${(data.amount / 1.19).toFixed(2)}</span>
            </div>
            <div class="amount-row">
                <span>MwSt. (19%):</span>
                <span>€${(data.amount - (data.amount / 1.19)).toFixed(2)}</span>
            </div>
            <div class="total-row">
                <span>Gesamtbetrag:</span>
                <span>€${data.amount.toFixed(2)}</span>
            </div>
        </div>
        
        <!-- Status Section -->
        <div class="status-section">
            <div class="status-text">
                Status: ${data.status === 'completed' ? 'ERFOLGREICH' : 'FEHLGESCHLAGEN'}
            </div>
        </div>
        
        <!-- Audit Information -->
        <div class="audit-info">
            <div class="audit-title">AUDIT-INFORMATIONEN</div>
            <div>Steuernummer: ${COMPANY_INFO.taxId}</div>
            <div>USt-IdNr.: ${COMPANY_INFO.vatNumber}</div>
            <div>Beleg erstellt: ${formattedDate} um ${formattedTime}</div>
            <div>System: SAHEEH POS v1.0</div>
            <div>Filiale: ${COMPANY_INFO.branchName}</div>
        </div>
        
        <!-- Footer -->
        <div class="footer">
            <div class="footer-info">
                <strong>Vielen Dank für Ihren Besuch!</strong>
            </div>
            <div class="footer-info">
                Bei Fragen wenden Sie sich bitte an unseren Kundenservice
            </div>
            <div class="footer-info">
                Tel: ${COMPANY_INFO.phone} | Email: ${COMPANY_INFO.email}
            </div>
            <div class="print-date">
                Gedruckt am: ${formattedDate} um ${formattedTime}
            </div>
        </div>
    </div>
</body>
</html>`;
  }

  // Generate Order Receipt HTML
  public generateOrderReceipt(data: OrderReportData): string {
    const currentDate = new Date();
    const formattedDate = currentDate.toLocaleDateString('en-US');
    const formattedTime = currentDate.toLocaleTimeString('en-US');
    
    return `
<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Bestellbeleg - ${data.orderNumber}</title>
    <style>
        @page {
            size: A4;
            margin: 0.5in;
        }
        
        * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
        }
        
        body {
            font-family: 'Arial', sans-serif;
            font-size: 12px;
            line-height: 1.4;
            color: #000;
            background: #fff;
        }
        
        .receipt {
            max-width: 100%;
            margin: 0 auto;
            padding: 20px;
            border: 2px solid #000;
        }
        
        .header {
            text-align: center;
            margin-bottom: 20px;
            border-bottom: 2px solid #000;
            padding-bottom: 15px;
        }
        
        .logo {
            max-width: 150px;
            height: auto;
            margin-bottom: 10px;
        }
        
        .company-name {
            font-size: 24px;
            font-weight: bold;
            margin-bottom: 5px;
            text-transform: uppercase;
        }
        
        .company-address {
            font-size: 11px;
            margin-bottom: 5px;
        }
        
        .branch-info {
            font-size: 12px;
            font-weight: bold;
            margin-top: 10px;
            padding: 8px;
            background-color: #f0f0f0;
            border: 1px solid #000;
        }
        
        .receipt-title {
            font-size: 18px;
            font-weight: bold;
            text-align: center;
            margin: 20px 0;
            text-transform: uppercase;
            border: 2px solid #000;
            padding: 10px;
            background-color: #e0e0e0;
        }
        
        .order-info {
            margin-bottom: 20px;
        }
        
        .info-row {
            display: flex;
            justify-content: space-between;
            margin-bottom: 5px;
            padding: 3px 0;
        }
        
        .info-label {
            font-weight: bold;
            min-width: 120px;
        }
        
        .info-value {
            text-align: right;
        }
        
        .items-table {
            width: 100%;
            border-collapse: collapse;
            margin: 20px 0;
            border: 2px solid #000;
        }
        
        .items-table th,
        .items-table td {
            border: 1px solid #000;
            padding: 8px;
            text-align: left;
        }
        
        .items-table th {
            background-color: #f0f0f0;
            font-weight: bold;
        }
        
        .items-table .quantity {
            text-align: center;
            width: 60px;
        }
        
        .items-table .price,
        .items-table .total {
            text-align: right;
            width: 80px;
        }
        
        .total-section {
            border: 2px solid #000;
            padding: 15px;
            margin: 20px 0;
            background-color: #f9f9f9;
        }
        
        .total-row {
            display: flex;
            justify-content: space-between;
            margin-bottom: 8px;
            font-size: 14px;
        }
        
        .grand-total {
            font-size: 18px;
            font-weight: bold;
            border-top: 2px solid #000;
            padding-top: 10px;
            margin-top: 10px;
        }
        
        .status-section {
            text-align: center;
            margin: 20px 0;
            padding: 15px;
            border: 2px solid #000;
            background-color: #d4edda;
        }
        
        .status-text {
            font-size: 16px;
            font-weight: bold;
            color: #155724;
        }
        
        .footer {
            margin-top: 30px;
            text-align: center;
            font-size: 10px;
            border-top: 1px solid #000;
            padding-top: 15px;
        }
        
        .footer-info {
            margin-bottom: 5px;
        }
        
        .audit-info {
            margin-top: 20px;
            padding: 10px;
            background-color: #f0f0f0;
            border: 1px solid #000;
            font-size: 10px;
        }
        
        .audit-title {
            font-weight: bold;
            margin-bottom: 5px;
            text-decoration: underline;
        }
        
        .print-date {
            text-align: right;
            font-size: 10px;
            margin-top: 10px;
            color: #666;
        }
        
        @media print {
            body { -webkit-print-color-adjust: exact; }
            .receipt { border: none; }
        }
    </style>
</head>
<body>
    <div class="receipt">
        <!-- Header with Logo and Company Info -->
        <div class="header">
            <img src="${COMPANY_INFO.logoUrl}" alt="SAHEEH Logo" class="logo" onerror="this.style.display='none'">
            <div class="company-name">${COMPANY_INFO.name}</div>
            <div class="company-address">
                ${COMPANY_INFO.address}<br>
                ${COMPANY_INFO.postalCode} ${COMPANY_INFO.city}<br>
                ${COMPANY_INFO.country}
            </div>
            <div class="company-address">
                Tel: ${COMPANY_INFO.phone} | Email: ${COMPANY_INFO.email}<br>
                Web: ${COMPANY_INFO.website}
            </div>
            <div class="branch-info">
                <strong>Filiale:</strong> ${COMPANY_INFO.branchName}<br>
                <strong>Adresse:</strong> ${COMPANY_INFO.branchAddress}<br>
                <strong>Tel:</strong> ${COMPANY_INFO.branchPhone}
            </div>
        </div>
        
        <!-- Receipt Title -->
        <div class="receipt-title">Bestellbeleg</div>
        
        <!-- Order Information -->
        <div class="order-info">
            <div class="info-row">
                <span class="info-label">Bestell-Nr:</span>
                <span class="info-value">${data.orderNumber}</span>
            </div>
            <div class="info-row">
                <span class="info-label">Kunde:</span>
                <span class="info-value">${(() => {
                  // Helper: Check if customer name is empty or default value
                  const isEmptyCustomerName = (name: string | undefined | null): boolean => {
                    if (!name) return true;
                    const trimmed = name.trim();
                    return trimmed === '' || trimmed === 'Laufkunde' || trimmed === 'Walk-in Customer';
                  };
                  
                  // If no name or address, show order type label:
                  // - Takeaway → "Abholung"
                  // - Delivery → "Lieferung"
                  // - Dine In → "Bestellung"
                  if (isEmptyCustomerName(data.customerName) && !data.customerAddress) {
                    if (data.order_type === 'takeaway') {
                      return 'Abholung';
                    } else if (data.order_type === 'delivery') {
                      return 'Lieferung';
                    } else if (data.order_type === 'dine_in') {
                      return data.tableNumber ? `Dine-In Tisch ${data.tableNumber}` : 'Dine-In';
                    }
                  }
                  return data.customerName || 'Kein Name eingegeben';
                })()}</span>
            </div>
            <div class="info-row">
                <span class="info-label">Datum:</span>
                <span class="info-value">${data.date}</span>
            </div>
            <div class="info-row">
                <span class="info-label">Uhrzeit:</span>
                <span class="info-value">${data.time}</span>
            </div>
            <div class="info-row">
                <span class="info-label">Kassierer:</span>
                <span class="info-value">${data.cashier || 'System'}</span>
            </div>
        </div>
        
        <!-- Items Table -->
        <table class="items-table">
            <thead>
                <tr>
                    <th>Artikel</th>
                    <th class="quantity">Anzahl</th>
                    <th class="price">Einzelpreis</th>
                    <th class="total">Gesamt</th>
                </tr>
            </thead>
            <tbody>
                ${data.items.map(item => `
                    <tr>
                        <td>${item.name}</td>
                        <td class="quantity">${item.quantity}</td>
                        <td class="price">€${item.price.toFixed(2)}</td>
                        <td class="total">€${item.total.toFixed(2)}</td>
                    </tr>
                `).join('')}
            </tbody>
        </table>
        
        <!-- Total Section -->
        <div class="total-section">
            <div class="total-row">
                <span>Zwischensumme (netto):</span>
                <span>€${(data.subtotal / 1.19).toFixed(2)}</span>
            </div>
            <div class="total-row">
                <span>MwSt. (19%):</span>
                <span>€${(data.subtotal - (data.subtotal / 1.19)).toFixed(2)}</span>
            </div>
            <div class="grand-total">
                <span>Gesamtbetrag:</span>
                <span>€${data.total.toFixed(2)}</span>
            </div>
        </div>
        
        <!-- Status Section -->
        <div class="status-section">
            <div class="status-text">
                Status: ${data.status.toUpperCase()}
            </div>
        </div>
        
        <!-- Audit Information -->
        <div class="audit-info">
            <div class="audit-title">AUDIT-INFORMATIONEN</div>
            <div>Steuernummer: ${COMPANY_INFO.taxId}</div>
            <div>USt-IdNr.: ${COMPANY_INFO.vatNumber}</div>
            <div>Beleg erstellt: ${formattedDate} um ${formattedTime}</div>
            <div>System: SAHEEH POS v1.0</div>
            <div>Filiale: ${COMPANY_INFO.branchName}</div>
        </div>
        
        <!-- Footer -->
        <div class="footer">
            <div class="footer-info">
                <strong>Vielen Dank für Ihren Besuch!</strong>
            </div>
            <div class="footer-info">
                Bei Fragen wenden Sie sich bitte an unseren Kundenservice
            </div>
            <div class="footer-info">
                Tel: ${COMPANY_INFO.phone} | Email: ${COMPANY_INFO.email}
            </div>
            <div class="print-date">
                Gedruckt am: ${formattedDate} um ${formattedTime}
            </div>
        </div>
    </div>
</body>
</html>`;
  }

  // Print the generated HTML
  public printReport(htmlContent: string): void {
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(htmlContent);
      printWindow.document.close();
      printWindow.focus();
      
      // Wait for images to load before printing
      printWindow.onload = () => {
        setTimeout(() => {
          printWindow.print();
          printWindow.close();
        }, 500);
      };
    } else {
      console.error('Could not open print window. Please check popup blockers.');
      alert('Could not open print window. Please check popup blockers.');
    }
  }

  // Print Payment Receipt
  public printPaymentReceipt(paymentData: any): void {
    const reportData: PaymentReportData = {
      paymentId: paymentData.id,
      orderId: paymentData.order_id,
      customerName: paymentData.customer_name,
      amount: paymentData.amount,
      paymentMethod: paymentData.payment_method,
      status: paymentData.status,
      transactionId: paymentData.transaction_id,
      date: new Date(paymentData.created_at).toLocaleDateString('en-US'),
      time: new Date(paymentData.created_at).toLocaleTimeString('en-US'),
      total: paymentData.amount,
      cashier: 'System',
      branchInfo: COMPANY_INFO.branchName
    };

    const htmlContent = this.generatePaymentReceipt(reportData);
    this.printReport(htmlContent);
  }

  // Print Order Receipt
  public printOrderReceipt(orderData: any): void {
    const reportData: OrderReportData = {
      orderId: orderData.id,
      orderNumber: orderData.order_number,
      customerName: orderData.customer_name,
      status: orderData.status,
      date: new Date(orderData.created_at).toLocaleDateString('en-US'),
      time: new Date(orderData.created_at).toLocaleTimeString('en-US'),
      items: orderData.items || [],
      subtotal: orderData.subtotal || orderData.total,
      tax: orderData.tax || 0,
      total: orderData.total,
      cashier: 'System',
      branchInfo: COMPANY_INFO.branchName,
      order_type: orderData.order_type,
      customerAddress: orderData.customer_address
    };

    const htmlContent = this.generateOrderReceipt(reportData);
    this.printReport(htmlContent);
  }
}

// Export singleton instance
export const printReportService = PrintReportService.getInstance();
